import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import {
  closeSchedule,
  ensureSchedule,
  listDayAnnotations,
  listShiftsForSchedule,
  publishSchedule,
  shiftHalf,
  updateScheduleNotes,
} from '@/lib/scheduling';
import { sendSchedulePublishEmail } from '@/lib/notifications';
import { notify } from '@/lib/notify';
import { useLocations } from '@/stores/locations';
import { useStaff } from '@/stores/staff';
import { useRole } from '@/stores/identity';
import { DayCell } from '@/components/calendar/DayCell';
import { StaffSidebar } from '@/components/calendar/StaffSidebar';
import { ShiftEditorModal } from '@/components/calendar/ShiftEditorModal';
import { DayAnnotationModal } from '@/components/calendar/DayAnnotationModal';
import { CopyMonthDialog } from '@/components/calendar/CopyMonthDialog';
import { CopyDayDialog } from '@/components/calendar/CopyDayDialog';
import { AddStaffModal } from '@/components/calendar/AddStaffModal';
import { PharmacistGapsBanner } from '@/components/PharmacistGapsBanner';
import {
  CrossOrgReadOnlyBanner,
  useIsReadOnlyCrossOrg,
} from '@/components/CrossOrgReadOnlyBanner';
import type {
  DayAnnotation,
  JobType,
  Location,
  Schedule,
  Shift,
} from '@/lib/types';

interface WeekdayHours {
  day_of_week: number;
  is_closed: boolean;
  open_time: string | null;
  close_time: string | null;
}

interface DateOverride {
  override_date: string;
  is_closed: boolean;
  open_time: string | null;
  close_time: string | null;
}

export default function ScheduleCalendar() {
  const { locationId, yyyymm } = useParams<{ locationId: string; yyyymm: string }>();
  const navigate = useNavigate();
  const role = useRole();
  // Cross-org admin: gate all edits. Schedule mutation RPCs (publish,
  // close, copyMonth, shift_*, day_annotation_*) refuse to act on rows
  // belonging to a different organization, so we hide write surfaces
  // when the viewer is a platform admin browsing another org's schedule.
  const baseCanEdit = role === 'owner' || role === 'manager';

  const locations = useLocations((s) => s.items);
  const locStatus = useLocations((s) => s.status);
  const loadLocations = useLocations((s) => s.load);

  const staffItems = useStaff((s) => s.items);
  const staffStatus = useStaff((s) => s.status);
  const loadStaff = useStaff((s) => s.load);

  const monthStart = useMemo(() => `${yyyymm}-01`, [yyyymm]);
  const location: Location | null = locations.find((l) => l.id === locationId) ?? null;

  const isReadOnlyCrossOrg = useIsReadOnlyCrossOrg(location?.organization_id);
  const canEdit = baseCanEdit && !isReadOnlyCrossOrg;

  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [annotations, setAnnotations] = useState<DayAnnotation[]>([]);
  const [weekdayHours, setWeekdayHours] = useState<WeekdayHours[]>([]);
  const [dateOverrides, setDateOverrides] = useState<DateOverride[]>([]);
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // v2 modals
  const [shiftModalOpen, setShiftModalOpen] = useState(false);
  const [shiftModalDate, setShiftModalDate] = useState<string>('');
  const [shiftModalHalf, setShiftModalHalf] = useState<'am' | 'pm' | null>(null);
  const [shiftModalShift, setShiftModalShift] = useState<Shift | null>(null);
  const [annotationModalOpen, setAnnotationModalOpen] = useState(false);
  const [annotationModalDate, setAnnotationModalDate] = useState<string>('');
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyDayOpen, setCopyDayOpen] = useState(false);
  const [copyDaySourceDate, setCopyDaySourceDate] = useState<string>('');
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [dropPrefill, setDropPrefill] = useState<{ staffId: string; startTime: string; endTime: string } | null>(null);

  const reload = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const sched = await ensureSchedule(locationId, monthStart);
      setSchedule(sched);
      setNotes(sched.notes ?? '');
      setNotesDirty(false);

      const [shiftsRes, annoRes, hoursRes, overridesRes] = await Promise.all([
        listShiftsForSchedule(sched.id),
        listDayAnnotations(locationId, monthStart),
        supabase.from('location_hours').select('day_of_week, is_closed, open_time, close_time').eq('location_id', locationId),
        supabase.from('location_hour_overrides').select('override_date, is_closed, open_time, close_time').eq('location_id', locationId).gte('override_date', monthStart).lt('override_date', monthEndOf(monthStart)),
      ]);
      setShifts(shiftsRes);
      setAnnotations(annoRes);
      setWeekdayHours((hoursRes.data ?? []) as WeekdayHours[]);
      setDateOverrides((overridesRes.data ?? []) as DateOverride[]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [locationId, monthStart]);

  useEffect(() => {
    if (locStatus === 'idle') void loadLocations();
    if (staffStatus === 'idle') void loadStaff();
    void reload();
  }, [locStatus, staffStatus, loadLocations, loadStaff, reload]);

  const cells = useMemo(() => buildCells(monthStart), [monthStart]);

  const closedDates = useMemo(() => {
    const out = new Set<string>();
    const overridesByDate = new Map(dateOverrides.map((o) => [o.override_date, o]));
    const weekdayMap = new Map(weekdayHours.map((w) => [w.day_of_week, w]));
    for (const c of cells) {
      const ov = overridesByDate.get(c.date);
      if (ov) { if (ov.is_closed) out.add(c.date); continue; }
      const wd = weekdayMap.get(c.dow);
      if (wd?.is_closed || (wd && (!wd.open_time || !wd.close_time))) out.add(c.date);
      if (!wd) out.add(c.date);
    }
    return out;
  }, [cells, dateOverrides, weekdayHours]);

  const shiftsByDate = useMemo(() => {
    const out = new Map<string, Shift[]>();
    for (const s of shifts) {
      if (!out.has(s.shift_date)) out.set(s.shift_date, []);
      out.get(s.shift_date)!.push(s);
    }
    return out;
  }, [shifts]);

  // Dates in this month that already have at least one non-cancelled shift.
  // Used by CopyDayDialog to grey out invalid target days (backend refuses
  // the whole op if any target has existing shifts).
  const occupiedDates = useMemo(() => {
    const out = new Set<string>();
    for (const s of shifts) if (!s.cancelled_at) out.add(s.shift_date);
    return out;
  }, [shifts]);

  const copyDaySourceShiftCount = useMemo(() => {
    if (!copyDaySourceDate) return 0;
    return (shiftsByDate.get(copyDaySourceDate) ?? []).filter((s) => !s.cancelled_at).length;
  }, [copyDaySourceDate, shiftsByDate]);

  const annotationsByDate = useMemo(() => {
    const out = new Map<string, DayAnnotation>();
    for (const a of annotations) out.set(a.annotation_date, a);
    return out;
  }, [annotations]);

  const staffJobTypes = useMemo(() => {
    const out = new Map<string, JobType>();
    for (const s of staffItems) out.set(s.id, s.job_type);
    return out;
  }, [staffItems]);

  // Compute the print-density tier from raw schedule data. The grid wrapper
  // gets a single class (density-typical/medium/dense) and the @media print
  // CSS sets a uniform row height for every cell in the month. See
  // docs/decisions/2026-05-11_print_density_redesign.md.
  const densityClass = useMemo(() => {
    const splitTime = location?.shift_split_time ?? '12:00:00';
    let maxShiftsInCol = 0;
    for (const c of cells) {
      const dayShifts = (shiftsByDate.get(c.date) ?? []).filter((s) => !s.cancelled_at);
      const am = dayShifts.filter((s) => shiftHalf(s.start_time, splitTime) === 'am').length;
      const pm = dayShifts.filter((s) => shiftHalf(s.start_time, splitTime) === 'pm').length;
      if (am > maxShiftsInCol) maxShiftsInCol = am;
      if (pm > maxShiftsInCol) maxShiftsInCol = pm;
    }
    const maxTags = annotations.reduce((m, a) => Math.max(m, a.tags.length), 0);
    if (maxShiftsInCol >= 6 || maxTags >= 4) return 'density-dense';
    if (maxShiftsInCol >= 4 || maxTags >= 3) return 'density-medium';
    return 'density-typical';
  }, [cells, shiftsByDate, annotations, location?.shift_split_time]);

  function gotoMonth(delta: number) {
    if (!yyyymm || !locationId) return;
    const [y, m] = yyyymm.split('-').map(Number);
    let ny = y, nm = m + delta;
    while (nm < 1) { nm += 12; ny -= 1; }
    while (nm > 12) { nm -= 12; ny += 1; }
    navigate(`/schedule/${locationId}/${ny}-${String(nm).padStart(2,'0')}`);
  }

  async function saveNotes() {
    if (!schedule) return;
    try {
      const updated = await updateScheduleNotes(schedule.id, notes);
      setSchedule(updated);
      setNotesDirty(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function onPublish() {
    if (!schedule) return;
    if (!confirm('Publish this schedule? Staff will see their shifts.')) return;
    try {
      const s = await publishSchedule(schedule.id);
      notify.success('Schedule published');
      setSchedule(s);
      // 0097: fire-and-forget the publish-event reminder fan-out. The
      // edge function is idempotent (notification_send_log unique index),
      // so a republish cannot double-send. Errors are logged to console
      // + audit; UI is intentionally quiet (audit-only visibility).
      void sendSchedulePublishEmail(s.id);
    } catch (err) {
      setError((err as Error).message);
    }
  }
  async function onClose() {
    if (!schedule) return;
    if (!confirm('Close this month? It becomes immutable.')) return;
    try {
      const s = await closeSchedule(schedule.id);
      notify.success('Schedule closed');
      setSchedule(s);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  if (!locationId || !yyyymm) return <div className="p-8">Invalid URL.</div>;


  return (
    <div className="flex flex-col h-full">
      {isReadOnlyCrossOrg && (
        <div className="calendar-print-hide-header px-4 pt-3">
          <CrossOrgReadOnlyBanner
            entityLabel="schedule"
            blockedActionsLabel="shift, day-annotation, publish, close, and copy-month actions"
          />
        </div>
      )}
      <header className="calendar-print-hide-header border-b border-border bg-muted/30 px-4 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Link to="/schedule" className="text-xs text-muted-foreground hover:underline">All locations</Link>
          <span className="text-foreground font-medium">{location?.name ?? '...'}</span>
          <button type="button" onClick={() => gotoMonth(-1)} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted">&lt;</button>
          <span className="text-sm font-medium tabular-nums">{formatMonth(monthStart)}</span>
          <button type="button" onClick={() => gotoMonth(1)} className="text-xs px-2 py-1 rounded border border-border hover:bg-muted">&gt;</button>
          {schedule && <ScheduleStatusBadge status={schedule.status} />}
        </div>
        <div className="flex items-center gap-2">
          {canEdit && schedule?.status === 'draft' && (
            <button type="button" onClick={() => setCopyOpen(true)} className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted">Copy from previous month...</button>
          )}
          <button type="button" onClick={() => window.print()} className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted">Print / PDF</button>
          {canEdit && schedule?.status === 'draft' && (
            <button type="button" onClick={onPublish} className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground hover:opacity-90">Publish</button>
          )}
          {canEdit && schedule?.status === 'published' && (
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-border hover:bg-muted">Close month</button>
          )}
        </div>
      </header>

      {error && (
        <div className="border-b border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-2">{error}</div>
      )}

      <div className="flex-1 flex">
        <StaffSidebar staff={staffItems} primaryLocationId={locationId} onAdd={() => setAddStaffOpen(true)} />

        <div className="flex-1 flex flex-col overflow-auto">
          <div className="schedule-print-container p-3 space-y-3">
            <div className="pharmacist-gaps-banner"><PharmacistGapsBanner locationId={locationId} monthStartDate={monthStart} /></div>

            {loading ? (
              <p className="text-sm text-muted-foreground">Loading calendar...</p>
            ) : (
              <>
              {/* Print-only branded header: status pill | pharmacy name | spacer.
                  Hidden on screen via @media print scoping in index.css. */}
              <div className="calendar-print-brand-header">
                <span className={'brand-status' + (schedule?.status === 'draft' ? ' draft' : schedule?.status === 'closed' ? ' closed' : '')}>
                  {(schedule?.status ?? 'draft').toUpperCase()}
                </span>
                <div className="brand-name-block">
                  <div className="brand-location">{location?.name ?? 'Schedule'}</div>
                  <div className="brand-month">{formatMonth(monthStart)}</div>
                </div>
                <div className="brand-spacer" />
              </div>
              <div className={`calendar-grid-wrapper ${densityClass} border border-border rounded-md overflow-hidden`}>
                <div className="calendar-dow-header grid grid-cols-7 bg-muted/40">
                  {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d) => (
                    <div key={d} className="px-2 py-1 text-center text-xs font-medium text-muted-foreground border-b border-border border-r last:border-r-0">
                      {d}
                      <div className="calendar-am-pm-marker text-[10px] text-muted-foreground">AM | PM</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7">
                  {cells.map((c) => (
                    <DayCell
                      key={c.date}
                      date={c.date}
                      dayNumber={c.day}
                      isOutOfMonth={c.isOutOfMonth}
                      isClosed={closedDates.has(c.date)}
                      shifts={shiftsByDate.get(c.date) ?? []}
                      staffJobTypes={staffJobTypes}
                      annotation={annotationsByDate.get(c.date) ?? null}
                      shiftSplitTime={location?.shift_split_time ?? '12:00:00'}
                      timeFormat={location?.time_format ?? 'am_pm'}
                      scheduleStatus={schedule?.status ?? 'draft'}
                      locationId={locationId}
                      yyyymm={yyyymm}
                      canEdit={canEdit}
                      onAddShift={(d, half) => { setShiftModalDate(d); setShiftModalHalf(half); setShiftModalShift(null); setDropPrefill(null); setShiftModalOpen(true); }}
                      onClickShift={(s) => { setShiftModalDate(s.shift_date); setShiftModalHalf(null); setShiftModalShift(s); setDropPrefill(null); setShiftModalOpen(true); }}
                      onEditAnnotation={(d) => { setAnnotationModalDate(d); setAnnotationModalOpen(true); }}
                      onDropStaff={(d, half, sid) => { setShiftModalDate(d); setShiftModalHalf(half); setShiftModalShift(null); setDropPrefill({ staffId: sid, startTime: defaultStartFor(d, half, weekdayHours, dateOverrides, location?.shift_split_time ?? '12:00:00'), endTime: defaultEndFor(d, half, weekdayHours, dateOverrides, location?.shift_split_time ?? '12:00:00') }); setShiftModalOpen(true); }}
                      onCopyDay={(d) => { setCopyDaySourceDate(d); setCopyDayOpen(true); }}
                    />
                  ))}
                </div>
              </div>
              </>
            )}

            <div className="calendar-monthly-notes border border-border rounded-md p-4 bg-card">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-medium">Monthly notes</h2>
                <span className="text-xs text-muted-foreground">visible on the printed schedule</span>
              </div>
              <textarea
                value={notes}
                onChange={(e) => { setNotes(e.target.value); setNotesDirty(true); }}
                placeholder="Add overall monthly reminders. Holiday hours, vacations, training notes, etc."
                rows={2}
                disabled={!canEdit || schedule?.status === 'closed'}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              {notesDirty && canEdit && (
                <div className="flex justify-end mt-2">
                  <button type="button" onClick={saveNotes} className="text-xs px-3 py-1.5 rounded bg-primary text-primary-foreground hover:opacity-90">Save notes</button>
                </div>
              )}
            </div>

            {/* Print-only role legend. Hidden on screen. */}
            <div className="calendar-print-legend">
              <span className="legend-title">Role key</span>
              <div className="legend-item"><span className="legend-swatch" style={{ background: '#E6F1FB', borderColor: '#185FA5' }} />Pharmacist / Manager</div>
              <div className="legend-item"><span className="legend-swatch" style={{ background: '#FAEEDA', borderColor: '#BA7517' }} />Technician</div>
              <div className="legend-item"><span className="legend-swatch" style={{ background: '#EAF3DE', borderColor: '#639922' }} />Assistant</div>
              <div className="legend-item"><span className="legend-swatch" style={{ background: '#EEEDFE', borderColor: '#534AB7' }} />Student</div>
              <div className="legend-item"><span className="legend-swatch legend-swatch-dashed" />Open / unassigned</div>
            </div>

            {/* Print-only branded footer: logo + "Powered by" left, metadata right. */}
            <div className="calendar-print-brand-footer">
              <div className="footer-brand">
                <svg className="footer-logo" viewBox="0 0 56 56" aria-label="PharmaSync">
                  <rect x="6" y="6" width="20" height="20" rx="3.5" fill="#1A2D5C"/>
                  <rect x="30" y="6" width="20" height="20" rx="3.5" fill="#4A7FBE"/>
                  <rect x="6" y="30" width="20" height="20" rx="3.5" fill="#9DCAE4"/>
                  <rect x="30" y="30" width="20" height="20" rx="3.5" fill="#5BA055"/>
                  <rect x="36" y="11" width="8" height="3" rx="1" fill="#FFFFFF" fillOpacity="0.75"/>
                  <rect x="36" y="17" width="6" height="3" rx="1" fill="#FFFFFF" fillOpacity="0.55"/>
                  <circle cx="40" cy="40" r="3.5" fill="#FFFFFF"/>
                  <line x1="13" y1="40" x2="19" y2="40" stroke="#1A2D5C" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                <span>Powered by <strong>PharmaSync</strong> &middot; <span className="footer-url">pharma-sync.ca</span></span>
              </div>
              <div className="footer-meta">
                {location?.name ?? ''} &middot; {formatMonth(monthStart)} &middot; {schedule?.status === 'draft' ? 'DRAFT' : schedule?.status?.toUpperCase()} &middot; printed {new Date().toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ShiftEditorModal
        open={shiftModalOpen}
        date={shiftModalDate}
        half={shiftModalHalf}
        shift={shiftModalShift}
        prefillStaffId={dropPrefill?.staffId ?? null}
        locationId={locationId}
        staff={staffItems}
        defaultStartTime={defaultStartFor(shiftModalDate, shiftModalHalf, weekdayHours, dateOverrides, location?.shift_split_time ?? '12:00:00')}
        defaultEndTime={defaultEndFor(shiftModalDate, shiftModalHalf, weekdayHours, dateOverrides, location?.shift_split_time ?? '12:00:00')}
        timeFormat={location?.time_format ?? 'am_pm'}
        onClose={() => setShiftModalOpen(false)}
        onSaved={() => reload()}
      />

      <CopyMonthDialog
        open={copyOpen}
        locationId={locationId}
        targetMonthStart={monthStart}
        onClose={() => setCopyOpen(false)}
        onDone={() => reload()}
      />

      <CopyDayDialog
        open={copyDayOpen}
        locationId={locationId}
        sourceDate={copyDaySourceDate}
        sourceShiftCount={copyDaySourceShiftCount}
        monthStart={monthStart}
        occupiedDates={occupiedDates}
        closedDates={closedDates}
        onClose={() => setCopyDayOpen(false)}
        onDone={() => reload()}
      />

      <AddStaffModal
        open={addStaffOpen}
        primaryLocationId={locationId}
        onClose={() => setAddStaffOpen(false)}
        onCreated={() => loadStaff()}
      />

      <DayAnnotationModal
        open={annotationModalOpen}
        date={annotationModalDate}
        locationId={locationId}
        availableTags={location?.day_annotation_tags ?? []}
        current={annotationsByDate.get(annotationModalDate) ?? null}
        onClose={() => setAnnotationModalOpen(false)}
        onSaved={() => reload()}
      />
    </div>
  );
}

function defaultStartFor(date: string, half: 'am' | 'pm' | null, weekly: WeekdayHours[], overrides: DateOverride[], split: string): string {
  if (!date || !half) return '09:00:00';
  const ov = overrides.find((o) => o.override_date === date);
  const [y, m, d] = date.split('-').map(Number);
  const dow = new Date(y, m - 1, d).getDay();
  const wd = weekly.find((w) => w.day_of_week === dow);
  const open = ov?.open_time ?? wd?.open_time ?? '09:00:00';
  return half === 'am' ? open : split;
}

function defaultEndFor(date: string, half: 'am' | 'pm' | null, weekly: WeekdayHours[], overrides: DateOverride[], split: string): string {
  if (!date || !half) return '17:00:00';
  const ov = overrides.find((o) => o.override_date === date);
  const dow = (() => { const [y,m,d] = date.split('-').map(Number); return new Date(y, m-1, d).getDay(); })();
  const wd = weekly.find((w) => w.day_of_week === dow);
  const close = ov?.close_time ?? wd?.close_time ?? '17:00:00';
  return half === 'am' ? split : close;
}

function ScheduleStatusBadge({ status }: { status: 'draft' | 'published' | 'closed' }) {
  const cls = status === 'draft' ? 'bg-amber-100 text-amber-700' : status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground';
  return <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{status}</span>;
}

function buildCells(monthStart: string) {
  const [y, m] = monthStart.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const dowOfFirst = first.getDay();
  const start = new Date(y, m - 1, 1 - dowOfFirst);
  const cells: { date: string; day: number; dow: number; isOutOfMonth: boolean }[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    cells.push({
      date,
      day: d.getDate(),
      dow: d.getDay(),
      isOutOfMonth: d.getMonth() !== m - 1,
    });
  }
  return cells;
}

function monthEndOf(monthStart: string): string {
  const [y, m] = monthStart.split('-').map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, '0')}-01`;
}

function formatMonth(monthStart: string): string {
  const [y, m] = monthStart.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}
